function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState } from 'react';
import { capitalize } from 'lodash-es';
import { Page, Button, TableList, Typography, CustomModal, PageEmptyState, TextButton, PageErrorState, Spinner, Searchbox, PageContentHeader, useOpenUnsavedChangesModal, Tabs } from 'seagull';
import { Plus } from 'seagull/icons';
import { serviceFromServiceTemplate } from "../../AddServicesButton/AddServicesPopover/AddServicesPopover";
import ExternalLinkButton from "../../ExternalLinkButton";
import { useOverlayContext, useStore, useStoreActions } from "../../../contexts";
import { useActiveServiceTemplates } from "../../../hooks/reactQuery/queries/useActiveServiceTemplates";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { modalSize } from "../../../infra/modals";
import { handlePromise } from "../../../infra/utils";
import { serviceTemplateModel, serviceModel } from "../../../models";
import { selectors } from "../../../store/state-selectors";
import { helpArticles } from "../../../utils/helpArticles";
import pluralize from "../../../utils/pluralize";
import ServiceBuilder from "../ServiceBuilder/ServiceBuilder";
import styles from "./ServicePicker.module.scss";
import { Mode, getColumnDefs, useNewServices } from "./ServicePicker.utils";
import ServicePickerEmptyIcon from "./ServicePickerEmptyIcon";
import ServicePickerRow from "./ServicePickerRow";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const labelFromMode = {
  [Mode.Services]: 'service',
  [Mode.Bundles]: 'bundle'
};
export default function ServicePicker(props) {
  const {
    agreementData,
    dismissModal,
    registerCanDismiss
  } = props;
  const {
    SERVICE_TEMPLATE_BUNDLE
  } = useFeatureFlags();
  const overlayContext = useOverlayContext();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const {
    logBI
  } = useStoreActions();
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const serviceTemplateApi = useServiceTemplateApi();
  const {
    fetchBundles: fetchBundlesFromStore
  } = useStoreActions();
  const {
    serviceTemplates: services,
    refresh: refreshServiceTemplates,
    error: servicesError
  } = useActiveServiceTemplates({
    refreshOnMount: true
  });
  const businessSettings = useStore(selectors.user.businessSettings);
  const [mode, setMode] = useState(Mode.Services);
  const [bundles, setBundles] = useState(null);
  const [bundlesError, setBundlesError] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState({});
  const {
    newServices,
    markServiceAsNew
  } = useNewServices();
  const currentData = useMemo(() => mode === Mode.Services ? services ? {
    services
  } : null : bundles, [mode, services, bundles]);
  const currentError = mode === Mode.Services ? Boolean(servicesError) : bundlesError;
  const currentHeader = 'Select services';
  const columnDefs = getColumnDefs(mode);
  const tabs = [{
    id: Mode.Services,
    label: 'Services'
  }, {
    id: Mode.Bundles,
    label: 'Bundles'
  }].map(_ref => {
    let {
      id,
      label
    } = _ref;
    return {
      id,
      value: id,
      component: label,
      testId: "".concat(id, "-tab")
    };
  });
  registerCanDismiss(async () => numSelected === 0 || openUnsavedChangesModal({
    title: 'Leave this page?',
    content: 'The selected service(s) will not be added to the agreement',
    primaryButtonLabel: 'Leave page'
  }));
  const filteredServices = useMemo(() => {
    if (!(currentData !== null && currentData !== void 0 && currentData.services)) return [];
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return currentData.services;
    return currentData.services.filter(service => service.name.toLowerCase().includes(normalizedSearch));
  }, [currentData, search]);
  const numSelected = useMemo(() => {
    let num = 0;
    for (const key in selectedRows) {
      if (selectedRows[key]) {
        num += 1;
      }
    }
    return num;
  }, [selectedRows]);
  function onToggleAllSelection(selectAll) {
    setSelectedRows(filteredServices.reduce((acc, _ref2) => {
      let {
        id
      } = _ref2;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }
  async function fetchBundles() {
    const bundles = await fetchBundlesFromStore();
    if (bundles) {
      setBundles({
        services: bundles.active
      });
    } else {
      setBundlesError(true);
      setBundles(null);
    }
  }
  async function createOrEditService() {
    const serviceNameBlacklist = currentData === null || currentData === void 0 ? void 0 : currentData.services.map(s => s.name);
    let newServiceId = '';
    const _onSave = async body => {
      const loaderName = 'servicePicker';
      overlayContext.startLoader(loaderName);
      try {
        const res = await serviceTemplateApi.create(body);
        newServiceId = res.id;
        await _onRefresh(mode);
        return {
          success: true
        };
      } catch (err) {
        overlayContext.showErrorToast('Failed to save service');
        return {
          success: false
        };
      } finally {
        overlayContext.stopLoader(loaderName);
      }
    };
    await overlayContext.showModal({
      transparentBackdrop: true,
      content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread(_objectSpread({}, p), {}, {
        serviceNameBlacklist: serviceNameBlacklist,
        agreementData: agreementData,
        serviceOrigin: agreementData.isActiveAgreement() ? 'agreement' : 'draft',
        isCreatingBundle: mode === Mode.Bundles,
        onSave: async output => {
          const body = serviceTemplateModel.fromService(output);
          return await handlePromise(_onSave(body));
        }
      }))
    });
    if (newServiceId) {
      markServiceAsNew(newServiceId);
      // Automatically select the new/edited service.
      if (!selectedRows[newServiceId]) {
        setSelectedRows(_objectSpread(_objectSpread({}, selectedRows), {}, {
          [newServiceId]: true
        }));
      }
    }
  }
  function applyAddServices() {
    if (!currentData) {
      return;
    }
    const selectedServiceIds = Object.keys(selectedRows).filter(key => !!selectedRows[key]);
    const selectedServices = currentData.services.filter(s => selectedServiceIds.includes(s.id));
    const translated = selectedServices.map(serviceTemplate => serviceFromServiceTemplate({
      serviceTemplate,
      agreementData,
      businessSettings
    }));
    dismissModal(translated, {
      force: true
    });
  }
  function _onRefresh(currentMode) {
    if (currentMode === Mode.Services) {
      refreshServiceTemplates();
    } else {
      fetchBundles();
    }
  }
  function renderNewServiceButton() {
    return currentData && !isMemberUser ? /*#__PURE__*/_jsx(Button, {
      prefixIcon: /*#__PURE__*/_jsx(Plus, {}),
      "data-testid": "service-picker-new-btn",
      onClick: () => createOrEditService(),
      children: "New ".concat(labelFromMode[mode])
    }) : null;
  }
  function renderSearchRow() {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.searchRow,
      children: [/*#__PURE__*/_jsx(Searchbox, {
        registerHotkey: true,
        value: search,
        onChange: event => setSearch(event.target.value),
        onClear: () => setSearch(''),
        "data-testid": "service-picker-search"
      }), SERVICE_TEMPLATE_BUNDLE && renderNewServiceButton()]
    });
  }
  function renderEmptyState() {
    switch (mode) {
      case Mode.Services:
        return /*#__PURE__*/_jsx(PageEmptyState, {
          illustration: /*#__PURE__*/_jsx(ServicePickerEmptyIcon, {}),
          message: "Your services will be listed here",
          actions: /*#__PURE__*/_jsx(TextButton, {
            "data-testid": "service-picker-empty-btn",
            onClick: () => createOrEditService(),
            children: "Create a new service"
          })
        });
      case Mode.Bundles:
        return /*#__PURE__*/_jsxs("div", {
          className: styles.noBundles,
          children: [/*#__PURE__*/_jsx(PageContentHeader, {
            children: renderSearchRow()
          }), /*#__PURE__*/_jsx(Typography, {
            variant: "h6",
            weight: "normal",
            children: "No bundles created yet"
          }), /*#__PURE__*/_jsx(ExternalLinkButton, {
            openInNewTab: true,
            href: helpArticles.bundles(),
            children: "Learn more about bundles"
          })]
        });
    }
  }
  const renderFooterContent = () => /*#__PURE__*/_jsxs(Page, {
    height: "100%",
    minWidth: "880px",
    skin: "secondary",
    header: currentHeader,
    subheader: !SERVICE_TEMPLATE_BUNDLE && /*#__PURE__*/_jsxs("div", {
      children: ["Search and select services from your Services page.", /*#__PURE__*/_jsx("br", {}), "If you don't see the one you want, you can create a new one."]
    }),
    actions: !SERVICE_TEMPLATE_BUNDLE && renderNewServiceButton(),
    children: [/*#__PURE__*/_jsx(PageContentHeader, {
      children: SERVICE_TEMPLATE_BUNDLE && /*#__PURE__*/_jsx(Tabs, {
        value: mode,
        className: styles.tabs,
        tabs: tabs,
        onChange: (_, newMode) => {
          if (newMode !== mode) {
            setMode(newMode);
            setSelectedRows({});
            setSearch('');
            _onRefresh(newMode);
          }
        }
      })
    }), currentData ? currentData.services.length ? /*#__PURE__*/_jsxs(TableList.Provider, {
      enableRowSelection: true,
      columnDefs: columnDefs,
      selectedRows: selectedRows,
      onSelectionChange: (selectedRows, changes) => {
        // We assume we can only select/deselect one service at a time.
        const changedKey = Object.keys(changes)[0];
        logBI(11100, {
          serviceId: changedKey,
          selected: changes[changedKey]
        });
        setSelectedRows(selectedRows);
      },
      numRows: filteredServices.length,
      numSelectedRows: numSelected,
      onToggleAllSelection: onToggleAllSelection,
      children: [/*#__PURE__*/_jsxs(PageContentHeader, {
        children: [renderSearchRow(), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(TableList.Header, {
          items: columnDefs,
          "data-testid": "service-picker-table-header"
        })]
      }), filteredServices.map((service, index) => /*#__PURE__*/_jsx(ServicePickerRow, {
        service: service,
        mode: mode,
        isNew: newServices[service.id],
        items: columnDefs.map(cd => cd.getItem({
          service,
          index
        })),
        onSelectionChange: () => {
          const changedKey = serviceModel.id(service);
          logBI(11100, {
            serviceId: changedKey,
            selected: service
          });
          setSelectedRows(_objectSpread(_objectSpread({}, selectedRows), {}, {
            [changedKey]: !selectedRows[changedKey]
          }));
        }
      }, index))]
    }) : renderEmptyState() : /*#__PURE__*/_jsx(Spinner, {
      size: 40,
      centeredInBox: true,
      centerBoxHeight: 200,
      "data-testid": "service-picker-loading"
    })]
  });
  return /*#__PURE__*/_jsx(CustomModal, _objectSpread(_objectSpread({
    className: styles.modal,
    "data-testid": "service-picker",
    onClose: () => dismissModal()
  }, modalSize.large), {}, {
    footer: /*#__PURE__*/_jsxs("div", {
      className: styles.footer,
      children: [!!numSelected && /*#__PURE__*/_jsxs(Typography, {
        "data-testid": "service-picker-num-selected",
        children: [pluralize(capitalize(labelFromMode[mode]), numSelected, true), " selected"]
      }), /*#__PURE__*/_jsx(Button, {
        "data-testid": "service-picker-apply-btn",
        disabled: !numSelected,
        onClick: applyAddServices,
        children: "Add ".concat(pluralize(labelFromMode[mode]))
      })]
    }),
    children: currentError ? /*#__PURE__*/_jsx(PageErrorState, {
      title: currentHeader,
      onRefresh: () => _onRefresh(mode),
      skin: "secondary"
    }) : renderFooterContent()
  }));
}